export interface RiskDimensionLevel {
  name: string;
  value: number;
  description?: string;
}

export interface RiskDimension {
  name: string;
  description?: string;
  levelCount: number;
  levels: RiskDimensionLevel[];
}

export interface RiskLevel {
  name: string;
  range: [number, number]; // [min, max]
  bgColor: string;
  textColor: string;
}

export interface RiskAssessmentMatrixConfig {
  dimensions: RiskDimension[];
  calculationMode: 'add' | 'multiply';
  riskLevelCount: number;
  riskLevels: RiskLevel[];
}

export interface RiskAssessmentMatrixItem {
  id: string;
  type: 'risk-assessment-matrix';
  label: string;
  name: string;
  required: boolean;
  config: RiskAssessmentMatrixConfig;
  stageId?: string;
  value?: {
    selectedValues: number[];
    calculatedResult: number;
    riskLevel: string;
  };
}

// 預設配置模板
export const DEFAULT_RISK_MATRIX_TEMPLATES = {
  // 5級配置（預設）
  level5: {
    dimensions: [
      {
        name: '嚴重度',
        description: '事故後果嚴重程度',
        levelCount: 5,
        levels: [
          { name: '輕微', value: 1, description: '輕微影響' },
          { name: '輕度', value: 2, description: '輕度影響' },
          { name: '一般', value: 3, description: '一般影響' },
          { name: '嚴重', value: 4, description: '嚴重影響' },
          { name: '很嚴重', value: 5, description: '極嚴重影響' }
        ]
      },
      {
        name: '可能性',
        description: '事故發生的可能性',
        levelCount: 5,
        levels: [
          { name: '很小', value: 1, description: '幾乎不可能' },
          { name: '小', value: 2, description: '不太可能' },
          { name: '大', value: 3, description: '可能發生' },
          { name: '很大', value: 4, description: '很可能發生' },
          { name: '幾乎確定', value: 5, description: '幾乎確定發生' }
        ]
      }
    ],
    calculationMode: 'multiply' as const,
    riskLevelCount: 5,
    riskLevels: [
      { name: '很低', range: [1, 4] as [number, number], bgColor: '#4CAF50', textColor: '#fff' },
      { name: '低', range: [5, 8] as [number, number], bgColor: '#8BC34A', textColor: '#fff' },
      { name: '中', range: [9, 15] as [number, number], bgColor: '#FFC107', textColor: '#000' },
      { name: '高', range: [16, 20] as [number, number], bgColor: '#FF9800', textColor: '#fff' },
      { name: '很高', range: [21, 25] as [number, number], bgColor: '#F44336', textColor: '#fff' }
    ]
  },
  
  // 3級配置
  level3: {
    dimensions: [
      {
        name: '影響程度',
        description: '事故影響程度',
        levelCount: 3,
        levels: [
          { name: '低', value: 1, description: '影響輕微' },
          { name: '中', value: 2, description: '影響一般' },
          { name: '高', value: 3, description: '影響嚴重' }
        ]
      },
      {
        name: '發生機率',
        description: '事故發生機率',
        levelCount: 3,
        levels: [
          { name: '低', value: 1, description: '不太可能' },
          { name: '中', value: 2, description: '可能發生' },
          { name: '高', value: 3, description: '很可能發生' }
        ]
      }
    ],
    calculationMode: 'multiply' as const,
    riskLevelCount: 3,
    riskLevels: [
      { name: '低風險', range: [1, 2] as [number, number], bgColor: '#4CAF50', textColor: '#fff' },
      { name: '中風險', range: [3, 6] as [number, number], bgColor: '#FFC107', textColor: '#000' },
      { name: '高風險', range: [7, 9] as [number, number], bgColor: '#F44336', textColor: '#fff' }
    ]
  },
  
  // 4級配置
  level4: {
    dimensions: [
      {
        name: '嚴重性',
        description: '事故嚴重性',
        levelCount: 4,
        levels: [
          { name: '輕微', value: 1, description: '輕微影響' },
          { name: '一般', value: 2, description: '一般影響' },
          { name: '嚴重', value: 3, description: '嚴重影響' },
          { name: '極嚴重', value: 4, description: '極嚴重影響' }
        ]
      },
      {
        name: '可能性',
        description: '事故可能性',
        levelCount: 4,
        levels: [
          { name: '很小', value: 1, description: '很小可能' },
          { name: '小', value: 2, description: '小可能' },
          { name: '大', value: 3, description: '大可能' },
          { name: '很大', value: 4, description: '很大可能' }
        ]
      }
    ],
    calculationMode: 'multiply' as const,
    riskLevelCount: 4,
    riskLevels: [
      { name: '低風險', range: [1, 4] as [number, number], bgColor: '#4CAF50', textColor: '#fff' },
      { name: '中風險', range: [5, 8] as [number, number], bgColor: '#FFC107', textColor: '#000' },
      { name: '高風險', range: [9, 12] as [number, number], bgColor: '#FF9800', textColor: '#fff' },
      { name: '極高風險', range: [13, 16] as [number, number], bgColor: '#F44336', textColor: '#fff' }
    ]
  }
};